import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useLazyCompanyGetEventReportQuery } from "@/services/(company)/api.events";
import { useState } from "react";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import { useLocale } from "@/hooks/useLocale";

export const EventReportButton = ({ eventId }: { eventId: string }) => {
  const { t } = useTranslation(["common", "company"]);
  const { formatDate } = useLocale();
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [getEventReport] = useLazyCompanyGetEventReportQuery();

  const handleGenerateEventReport = async () => {
    const toastId = toast.loading(t("company:exports.generating_report"));

    try {
      setIsGeneratingReport(true);
      const res = await getEventReport({ eventId }).unwrap();
      const { allParticipants, event, stats } = res.data;

      const breaksSummary =
        event.breaks && event.breaks.length
          ? event.breaks
              .map(
                (b) =>
                  `${b.title || t("company:exports.break_label")} (${formatDate(
                    new Date(b.startDateTime),
                    "hh:mm a",
                  )} - ${formatDate(new Date(b.endDateTime), "hh:mm a")})`,
              )
              .join("; ")
          : t("company:exports.none");

      // 1. Prepare Overview Data (Key-Value pairs)
      const overviewData = [
        {
          [t("company:exports.col_detail")]: t("company:exports.event_title"),
          [t("company:exports.col_value")]: event.title,
        },
        {
          [t("company:exports.col_detail")]: t("company:exports.col_status"),
          [t("company:exports.col_value")]: event.status.toUpperCase(),
        },
        {
          [t("company:exports.col_detail")]: t("company:exports.event_date"),
          [t("company:exports.col_value")]: formatDate(
            new Date(event.startDateTime),
            "Pp",
          ),
        },
        {
          [t("company:exports.col_detail")]: t("company:exports.event_type"),
          [t("company:exports.col_value")]: event.type,
        },
        {
          [t("company:exports.col_detail")]: t(
            "company:exports.event_language",
          ),
          [t("company:exports.col_value")]:
            event.language || t("company:exports.na"),
        },
        {
          [t("company:exports.col_detail")]: t("company:exports.meeting_link"),
          [t("company:exports.col_value")]:
            event.location || t("company:exports.na"),
        },
        {
          [t("company:exports.col_detail")]: t("company:exports.breaks"),
          [t("company:exports.col_value")]: breaksSummary,
        },
        {
          [t("company:exports.col_detail")]: "",
          [t("company:exports.col_value")]: "",
        }, // Spacer
        {
          [t("company:exports.col_detail")]: t(
            "company:exports.statistics_divider",
          ),
          [t("company:exports.col_value")]: "",
        },
        {
          [t("company:exports.col_detail")]: t("company:exports.total_invited"),
          [t("company:exports.col_value")]: stats.totalInvited,
        },
        {
          [t("company:exports.col_detail")]: t("company:exports.accepted"),
          [t("company:exports.col_value")]: stats.accepted,
        },
        {
          [t("company:exports.col_detail")]: t("company:exports.rejected"),
          [t("company:exports.col_value")]: stats.rejected,
        },
        {
          [t("company:exports.col_detail")]: t("company:exports.pending"),
          [t("company:exports.col_value")]: stats.pending,
        },
      ];

      // 2. Preparing Participants Data
      const participantsData = allParticipants.map((p) => ({
        [t("company:exports.col_name")]: p.name,
        [t("company:exports.col_email")]: p.email,
        [t("company:exports.col_type")]: p.isExternal
          ? t("company:exports.external")
          : t("company:exports.internal"),
        [t("company:exports.col_status")]: p.status.toUpperCase(),
        [t("company:exports.col_invited_at")]: formatDate(
          new Date(p.invitedAt),
          "P",
        ),
        [t("company:exports.col_responded_at")]: p.respondedAt
          ? formatDate(new Date(p.respondedAt), "P")
          : t("company:exports.no_response"),
        [t("company:exports.col_note")]: p.responseNote || "",
      }));

      // 3. Creating Workbook and Sheets
      const wb = XLSX.utils.book_new();
      const wsOverview = XLSX.utils.json_to_sheet(overviewData);
      const wsParticipants = XLSX.utils.json_to_sheet(participantsData);

      // 4. Appending sheets to workbook
      XLSX.utils.book_append_sheet(
        wb,
        wsOverview,
        t("company:exports.sheet_event_overview"),
      );
      XLSX.utils.book_append_sheet(
        wb,
        wsParticipants,
        t("company:exports.sheet_participants"),
      );

      // 5. Triggering Download
      const fileName = `Event_Report_${event.title.replace(/\s+/g, "_")}.xlsx`;
      XLSX.writeFile(wb, fileName);

      toast.success(t("company:exports.report_generated"), { id: toastId });
    } catch (e) {
      console.error(e);
      toast.error(t("company:exports.failed_to_generate_report"), {
        id: toastId,
      });
    } finally {
      setIsGeneratingReport(false);
    }
  };

  return (
    <Button
      variant="outline-success"
      onClick={handleGenerateEventReport}
      isLoading={isGeneratingReport}
      className="min-w-32"
    >
      {t("common:buttons.export")}
    </Button>
  );
};
